import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';

interface AdminOption {
  title: string;
  description: string;
  icon: string;
  route: string;
  requiredRoles: string[];
  color: string; // Tailwind color class
}

@Component({
  selector: 'app-credit-option',
  templateUrl: './credit-option.component.html',
  styleUrls: [] // Eliminamos el CSS personalizado
})
export class CreditOptionComponent implements OnInit {
  options: AdminOption[] = [
    {
      title: 'Usuarios en Línea',
      description: 'Monitorea usuarios activos y sesiones en tiempo real',
      icon: 'fas fa-users',
      route: '/console/online',
      requiredRoles: [],
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Estatus de Aprobación de Usuarios',
      description: 'Revisa y gestiona el estado de aprobación de nuevos usuarios',
      icon: 'fas fa-user-check',
      route: '/console/constacts-approved',
      requiredRoles: ['OWNER', 'ANALIST'],
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: 'Gestión de Usuarios',
      description: 'Administra perfiles, permisos y configuraciones de usuarios del sistema',
      icon: 'fas fa-users-cog',
      route: '/console/users',
      requiredRoles: ['OWNER'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Reportes y Exportación',
      description: 'Genera reportes detallados y exporta datos en múltiples formatos',
      icon: 'fas fa-chart-bar',
      route: '/console/export',
      requiredRoles: [],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Notificaciones por Canales',
      description: 'Configura y envía notificaciones masivas a través de diferentes canales',
      icon: 'fas fa-bell',
      route: '/console/notificacion-xlsx',
      requiredRoles: ['OWNER'],
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Gestión de Notificaciones',
      description: 'Administra plantillas, historial y configuraciones de notificaciones',
      icon: 'fas fa-comments',
      route: '/console/notifications',
      requiredRoles: ['OWNER'],
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Gestión de Planes',
      description: 'Configura planes de crédito, límites y condiciones comerciales',
      icon: 'fab fa-product-hunt',
      route: '/console/plans',
      requiredRoles: ['OWNER'],
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      title: 'Tasas de Cambio',
      description: 'Administra las tasas de cambio y conversiones monetarias del sistema',
      icon: 'fas fa-dollar-sign',
      route: '/console/rates',
      requiredRoles: ['OWNER'],
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Gestión de Cuentas',
      description: 'Administra cuentas bancarias, métodos de pago y configuraciones financieras',
      icon: 'fas fa-university',
      route: '/console/accounts-admin',
      requiredRoles: ['OWNER'],
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Carga Masiva de Clientes',
      description: 'Importa grandes volúmenes de clientes mediante archivos Excel',
      icon: 'fas fa-upload',
      route: '/console/carga-masiva-cleinte-xlsx',
      requiredRoles: ['OWNER'],
      color: 'bg-orange-100 text-orange-600'
    },
    {
      title: 'Carga Masiva de Aprobaciones',
      description: 'Procesa aprobaciones en lote para agilizar operaciones administrativas',
      icon: 'fas fa-check-double',
      route: '/console/carga-masiva-aprobaciones-xlsx',
      requiredRoles: ['OWNER'],
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Aumento Masivo de Cupos',
      description: 'Incrementa límites de crédito de múltiples clientes simultáneamente',
      icon: 'fas fa-arrow-up',
      route: '/console/carga-masiva-cupos',
      requiredRoles: ['OWNER'],
      color: 'bg-teal-100 text-teal-600'
    },
    {
      title: 'Aumento Masivo de Cupos con Rewards',
      description: 'Incrementa límites de crédito de múltiples clientes y ajusta sus puntos, nivel y plan en rewards',
      icon: 'fas fa-arrow-up',
      route: '/console/carga-masiva-cupos-rewards',
      requiredRoles: ['OWNER'],
      color: 'bg-teal-100 text-teal-600'
    },
    {
      title: 'Inactivar Cupos Masivamente',
      description: 'Suspende o inactiva múltiples cupos de crédito de forma masiva',
      icon: 'fas fa-ban',
      route: '/console/inactivate-quota',
      requiredRoles: ['OWNER'],
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Reactivaciones Masivas con Penalidades (C004)',
      description: 'Genera fees de reactivación C004 de forma masiva mediante carga de Excel',
      icon: 'fas fa-file-invoice-dollar',
      route: '/console/credito/reactivaciones-masivas',
      requiredRoles: ['OWNER'],
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: 'Carga Masiva de Códigos',
      description: 'Asigna códigos únicos a personas de forma masiva y automatizada',
      icon: 'fas fa-qrcode',
      route: '/console/code-dynamic',
      requiredRoles: ['OWNER'],
      color: 'bg-gray-100 text-gray-600'
    },
    {
      title: 'Códigos',
      description: 'Administra códigos del sistema y asignación de métodos de pago',
      icon: 'fas fa-code',
      route: '/console/codigos',
      requiredRoles: ['OWNER'],
      color: 'bg-slate-100 text-slate-600'
    }
  ];

  userRoles: string[] = [];

  constructor(public _auth: AuthService) {}

  ngOnInit(): void {
    this.userRoles = Array.isArray(this._auth.sessionUser?.profile) ? this._auth.sessionUser.profile : [];
  }

  hasPermission(option: AdminOption): boolean {
    if (this._auth.hasRbac$.getValue()) {
      return this._auth.hasModuleAccess('credito');
    }
    if (!option.requiredRoles || option.requiredRoles.length === 0) return true;
    return option.requiredRoles.some(role => this.userRoles.includes(role));
  }
}
